import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {children}
    </div>
  );
}

function SubSection({ title, children }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </div>
  );
}

function P({ children }) {
  return <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>;
}

function UL({ children }) {
  return <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside">{children}</ul>;
}

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-12">
      <Button variant="ghost" onClick={() => navigate(-1)}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>

      <div>
        <h1 className="text-2xl font-bold">TruContext Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mt-1">Effective Date: March 22, 2026 | Last Updated: March 22, 2026</p>
      </div>

      <P>Alpha Collective LLC ("Company," "we," "us," or "our") operates the TruContext platform, APIs, CLI tools, and related services (collectively, the "Service"). This Privacy Policy describes how we collect, use, and protect information when you use the Service.</P>

      <Section title="1. Information We Collect">
        <SubSection title="Account Information">
          <P>When you create an account, we collect:</P>
          <UL>
            <li>Email address (via OAuth through AWS Cognito)</li>
            <li>Authentication tokens (stored locally on your device, not on our servers)</li>
          </UL>
          <P>We do not collect passwords. Authentication is handled entirely through OAuth providers.</P>
        </SubSection>

        <SubSection title="Your Data (User Content)">
          <P>When you use the Service, you may ingest, store, and process:</P>
          <UL>
            <li>Text content, documents, images, audio, and other files</li>
            <li>Entities, relationships, and structural data</li>
            <li>Metadata you provide (author hints, source medium, filenames)</li>
            <li>Confidence scores, temporal flags, and context relationships</li>
          </UL>
          <P><strong className="text-foreground">You own Your Data.</strong></P>
        </SubSection>

        <SubSection title="Usage Data">
          <P>We automatically collect:</P>
          <UL>
            <li>API request logs (endpoint, method, timestamp, response status)</li>
            <li>Error logs for debugging and service reliability</li>
            <li>Aggregate usage metrics (ingest volume, query count, API key usage)</li>
          </UL>
          <P>We do not collect or log the content of your API requests or responses in our usage analytics.</P>
        </SubSection>

        <SubSection title="Device and CLI Information">
          <P>The CLI tool stores authentication tokens locally at <code className="bg-muted px-1.5 py-0.5 rounded text-xs">~/.trucontext/credentials.json</code> with restricted file permissions. We do not collect device identifiers, IP addresses for tracking purposes, or telemetry from the CLI tool.</P>
        </SubSection>
      </Section>

      <Section title="2. How We Use Your Data">
        <SubSection title="Your Content">
          <UL>
            <li><strong className="text-foreground">To provide the Service:</strong> We process your content through our AI pipeline solely to operate the features you use.</li>
            <li><strong className="text-foreground">Within your tenant only:</strong> Your Data is isolated to your tenant. Other users cannot access it.</li>
            <li><strong className="text-foreground">Not for training:</strong> We do not use Your Data to train machine learning models — ours or anyone else's — unless you provide separate, explicit written consent.</li>
            <li><strong className="text-foreground">Not for sale:</strong> We do not sell, rent, or share Your Data with third parties for their commercial purposes.</li>
          </UL>
        </SubSection>
      </Section>

      <Section title="3. Third-Party Services">
        <P>We use the following third-party services to operate the platform:</P>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left p-3 font-medium text-foreground">Service</th>
                <th className="text-left p-3 font-medium text-foreground">Purpose</th>
                <th className="text-left p-3 font-medium text-foreground">Data Shared</th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              <tr className="border-b"><td className="p-3 font-medium text-foreground">AWS</td><td className="p-3">Infrastructure & auth</td><td className="p-3">Stored in AWS US regions</td></tr>
              <tr className="border-b"><td className="p-3 font-medium text-foreground">Neo4j Aura</td><td className="p-3">Graph database</td><td className="p-3">Stored in knowledge graph</td></tr>
              <tr className="border-b"><td className="p-3 font-medium text-foreground">Anthropic</td><td className="p-3">AI processing</td><td className="p-3">Content sent for extraction</td></tr>
              <tr className="border-b"><td className="p-3 font-medium text-foreground">Google AI</td><td className="p-3">Alt AI processing</td><td className="p-3">Content if configured</td></tr>
              <tr><td className="p-3 font-medium text-foreground">Stripe</td><td className="p-3">Payments</td><td className="p-3">Email & billing info</td></tr>
            </tbody>
          </table>
        </div>
        <P>These providers have committed to not training on API inputs.</P>
      </Section>

      <Section title="4. Data Retention">
        <UL>
          <li><strong className="text-foreground">Your Data:</strong> Retained as long as your account is active. Upon termination, you may request export within 30 days.</li>
          <li><strong className="text-foreground">Heartbeat events:</strong> Automatically expire after 30 days.</li>
          <li><strong className="text-foreground">Heartbeat thoughts:</strong> Automatically expire after 90 days.</li>
          <li><strong className="text-foreground">Usage logs:</strong> Retained for up to 12 months, then deleted.</li>
          <li><strong className="text-foreground">Account information:</strong> Retained until you delete your account.</li>
        </UL>
      </Section>

      <Section title="5. Data Security">
        <UL>
          <li><strong className="text-foreground">Encryption at rest:</strong> All data stored in DynamoDB, S3, and Neo4j is encrypted.</li>
          <li><strong className="text-foreground">Encryption in transit:</strong> All API communication uses TLS/HTTPS.</li>
          <li><strong className="text-foreground">Tenant isolation:</strong> Every database query is scoped to your tenant ID. Cross-tenant access is architecturally prevented.</li>
          <li><strong className="text-foreground">API key security:</strong> Managed through AWS API Gateway with usage plans and throttling.</li>
          <li><strong className="text-foreground">Authentication:</strong> OAuth 2.0 with PKCE flow. No passwords stored.</li>
          <li><strong className="text-foreground">Local credential security:</strong> CLI tokens stored with 600 file permissions.</li>
        </UL>
      </Section>

      <Section title="6. Your Rights">
        <P>You have the right to:</P>
        <UL>
          <li><strong className="text-foreground">Access</strong> your data at any time through the API, CLI, or dashboard.</li>
          <li><strong className="text-foreground">Export</strong> your data in structured format upon request.</li>
          <li><strong className="text-foreground">Delete</strong> your data by deleting entities, content, or your entire account.</li>
          <li><strong className="text-foreground">Correct</strong> your data by updating entities and ingesting corrections.</li>
          <li><strong className="text-foreground">Object</strong> to specific processing by contacting us.</li>
          <li><strong className="text-foreground">Withdraw consent</strong> for any optional processing at any time.</li>
        </UL>
        <P>For data subject requests, contact us at privacy@alphacollective.com.</P>
      </Section>

      <Section title="7. Children's Privacy">
        <P>The Service is not directed to individuals under 16. We do not knowingly collect information from children under 16.</P>
      </Section>

      <Section title="8. International Data Transfers">
        <P>Your Data is processed and stored in the United States (AWS US-East-1 region). By using the Service, you consent to this transfer.</P>
      </Section>

      <Section title="9. Changes to This Policy">
        <P>We may update this Privacy Policy from time to time. We will notify you of material changes via email or through the Service.</P>
      </Section>

      <Section title="10. Contact">
        <P><strong className="text-foreground">Alpha Collective LLC</strong><br />Email: privacy@alphacollective.com<br />Website: trucontext.ai</P>
      </Section>
    </div>
  );
}
